/**
 * Present provider-scoped connector authority inside the organization-user
 * role editor. Vault still owns grant persistence and authorization; this
 * component only presents the grants alongside the user's other org roles.
 */
import { Checkbox, Label } from '@/components/ui'
import { connectorProviderLabel } from '../../lib/provider-labels'
import type { ConnectorProvider } from '../types'

interface ConnectorAdminRoleCheckboxesProps {
  providers: readonly ConnectorProvider[]
  selectedProviders: readonly ConnectorProvider[]
  onChange: (providers: ConnectorProvider[]) => void
  disabled?: boolean
}

export function ConnectorAdminRoleCheckboxes({
  providers,
  selectedProviders,
  onChange,
  disabled = false,
}: ConnectorAdminRoleCheckboxesProps) {
  const selected = new Set(selectedProviders)
  const toggle = (provider: ConnectorProvider) => {
    onChange(
      selected.has(provider)
        ? selectedProviders.filter(current => current !== provider)
        : [...selectedProviders, provider],
    )
  }

  return (
    <section
      aria-labelledby="connector-admin-role-heading"
      data-testid="connector-admin-role-options"
      className="space-y-1"
    >
      <div className="space-y-1 pb-1">
        <p
          id="connector-admin-role-heading"
          className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
        >
          Connector administrator roles
        </p>
        <p className="text-xs text-muted-foreground">
          Configure the selected integrations across this organization without
          granting workspace or document access.
        </p>
      </div>
      {providers.map(provider => {
        const id = `connector-admin-role-${provider}`
        return (
          <div
            key={provider}
            className={`-mx-2 flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors ${
              disabled ? 'opacity-60' : 'hover:bg-muted/60'
            }`}
          >
            <Checkbox
              id={id}
              data-testid={id}
              checked={selected.has(provider)}
              disabled={disabled}
              onCheckedChange={() => toggle(provider)}
            />
            <Label
              htmlFor={id}
              className={`flex-1 font-normal ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {connectorProviderLabel(provider)}
            </Label>
          </div>
        )
      })}
    </section>
  )
}
